/**
 * A ficha do funcionário — módulo SST (03/11/2026, pedido do Lucas).
 *
 * Clicou no nome em Funcionários ou em Pendências, cai aqui: quem é e onde
 * está, o semáforo (ASO, treinamentos, EPI, pendências), as seções por
 * assunto e, no fim, a linha do tempo com tudo junto. A única coisa que esta
 * tela grava é a pasta de documentos (um PDF ou foto por vez, com tipo e
 * data); o resto vem das cargas e, no futuro, dos formulários de cada fato.
 *
 * Quem tem o módulo SST vê tudo, inclusive resultado de exame (decisão do
 * Lucas, 03/11).
 */

import { api, ErroApi } from '../api.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';
import { dataHora, escapar } from '../comum/formato.js';

/** Os mesmos tipos do servidor (sst/documentos.ts). */
export const TIPOS_DOCUMENTO: [string, string][] = [
  ['contrato', 'Contrato de trabalho / experiência'],
  ['registro_empregado', 'Registro de empregado'],
  ['ctps', 'CTPS'],
  ['documento_pessoal', 'Documento pessoal (RG, CPF, comprovante)'],
  ['cnh', 'CNH'],
  ['termo', 'Termo assinado (LGPD, celular, transferência, aceite)'],
  ['ordem_servico', 'Ordem de serviço (NR-1) em papel'],
  ['certificado', 'Certificado de treinamento (em papel)'],
  ['ficha_epi', 'Ficha / termo de entrega de EPI (em papel)'],
  ['cartao_vacina', 'Cartão de vacina'],
  ['exame', 'Exame complementar / laudo'],
  ['rescisao', 'Rescisão (TRCT, quitação, dispensa)'],
  ['outro', 'Outro'],
];
const NOME_TIPO_DOC = new Map(TIPOS_DOCUMENTO);

type Validade = 'sem_registro' | 'valido' | 'a_vencer' | 'vencido' | 'sem_vencimento';

interface Ficha {
  hoje: string;
  funcionario: {
    id: number;
    cpf: string;
    nome: string;
    nascimento: string | null;
    idade: number | null;
    sexo: string | null;
    matricula_dominio: string | null;
    admissao: string | null;
    desligamento: string | null;
    cnh_categoria: string | null;
    vinculo: string;
    origem: string;
    importado_em: string | null;
    criado_em: string;
    atualizado_em: string;
    obs: string | null;
    retencao_ate: string | null;
    funcao: string | null;
    cbo: string | null;
    ghe: string | null;
    ghe_nome: string | null;
    ghe_riscos: unknown;
    atividades_padrao: string[];
    pessoa_nome: string | null;
    pessoa_email: string | null;
    lotacao_atual: { obra: string | null; obra_codigo: string | null; centro_custo: string | null; desde: string } | null;
    afastado: { motivo: string | null; inicio: string; fim: string | null } | null;
  };
  semaforo: {
    aso: { situacao: Validade; data: string | null; tipo: string | null; validade: string | null };
    treinamentos: { exigidos: number; validos: number; a_vencer: number; vencidos: number; sem_registro: number };
    epi: { exigidos: number; sem_matriz: boolean; entregues: number; sem_entrega: number; troca_vencida: number; em_uso: number };
    pendencias: number;
    afastado: { motivo: string | null; inicio: string; fim: string | null } | null;
    toxicologico: { situacao: Validade; ultimo: string | null } | null;
  };
  lotacoes: { id: number; inicio: string; fim: string | null; centro_custo: string | null; origem: string; obra: string | null; obra_codigo: string | null }[];
  asos: {
    id: number;
    tipo: string;
    data_exame: string;
    validade: string | null;
    resultado: string | null;
    restricoes: Record<string, boolean | null>;
    medico_examinador: string | null;
    crm_examinador: string | null;
    medico_coordenador: string | null;
    crm_coordenador: string | null;
    clinica: string | null;
    origem: string;
    ref_externa: string | null;
    arquivo: string | null;
    conferido_em: string | null;
    conferido_por: string | null;
    dispensa_motivo: string | null;
    esocial_status: string;
    requer_revisao: boolean;
    exames: { codigo: string; nome: string; data: string; resultado: string | null; laudo: string | null }[];
  }[];
  toxicologicos: { id: number; tipo: string; data_coleta: string; data_resultado: string | null; laboratorio: string | null; medico: string | null; resultado: string | null; contraprova: boolean; arquivo: string | null; esocial_status: string; obs: string | null }[];
  vacinacoes: { id: number; vacina: string; dose: number; data: string; proxima_em: string | null; comprovante: string | null; origem: string }[];
  afastamentos: { id: number; inicio: string; fim: string | null; motivo: string | null; origem: string; obs: string | null }[];
  cats: { id: number; tipo: string; data: string; hora: string | null; natureza: string; afastou: boolean; ultimo_dia: string | null; local_descricao: string | null; descricao: string | null; gravidade: string; numero_inss: string | null; arquivo: string | null; esocial_status: string; obra: string | null }[];
  certificados: { id: number; codigo: string; treinamento: string; data: string; carga_h: number | null; instrutor: string | null; entidade: string | null; origem_certificado: string; validade: string | null; arquivo: string | null; origem: string; requer_revisao: boolean }[];
  ordens_servico: { id: number; gerada_em: string; validade: string; arquivo: string | null; origem: string; funcao: string | null; documento: string | null }[];
  epi: { id: number; epi: string; epi_tipo: string; ca: string | null; data: string; quantidade: number; ca_congelado: string | null; ca_validade_congelada: string | null; motivo: string; troca_prevista: string | null; troca_justificativa: string | null; pdf: string | null; origem: string; obra: string | null; tecnico: string | null; baixa: { data: string; motivo: string; estado: string | null } | null }[];
  atividades: { id: number; chave: string; nome: string; desde: string; ate: string | null; origem: string }[];
  vinculos: { sistema: string; chave: string; confianca: string; criado_em: string }[];
  pendencias: { id: number; tipo: string; referencia: Record<string, unknown>; vence_em: string | null; origem: string; criada_em: string; resolvida_em: string | null; resolvida_por: string | null }[];
  eventos: { id: number; tipo: string; data: string; dados: Record<string, unknown>; origem: string; criado_em: string }[];
  documentos: { id: number; tipo: string; titulo: string | null; data: string | null; url: string | null; tipo_mime: string; origem: string; ref_externa: string | null; obs: string | null; criado_em: string; enviado_por: string | null }[];
  caixa: { id: number; tipo: string; situacao: string; descricao: string | null; nome_arquivo: string | null; criado_em: string; registro_tipo: string | null; registro_id: number | null; remetente: string; remetente_nome: string | null; assunto: string | null; recebida_em: string }[];
  exigencias: {
    treinamentos: { treinamento_id: number; codigo: string; nome: string; carga_inicial_h: number | null; carga_periodica_h: number | null; validade_meses: number | null; exigencia: string; procedencia: string; doc_tipo: string; atividade: string | null; ultimo_em: string | null; valido_ate: string | null; situacao: Validade }[];
    epi: { id: number; tipo_epi: string; quantidade: number; exigencia: string; procedencia: string; doc_tipo: string; atividade: string | null; entregue_em: string | null; troca_prevista: string | null; epi: string | null; situacao: string }[];
  };
}

const CAUSA: Record<string, string> = { doenca: 'doença', acidente: 'acidente', maternidade: 'maternidade', aposentadoria_invalidez: 'aposentadoria por invalidez', outro: 'outro' };
const TIPO_EXAME: Record<string, string> = { admissional: 'admissional', periodico: 'periódico', retorno: 'retorno ao trabalho', mudanca_risco: 'mudança de risco', demissional: 'demissional' };
const RESULTADO: Record<string, string> = { apto: 'apto', apto_restricao: 'apto com restrição', inapto: 'INAPTO' };
const RESTRICAO: Record<string, string> = { altura: 'altura', confinado: 'espaço confinado', radiacao: 'radiação', eletricidade: 'eletricidade', conducao_veiculo: 'direção de veículo', manipulacao_alimentos: 'alimentos' };
const ORIGEM: Record<string, string> = { dominio: 'folha (Domínio)', portal_asmetro: 'portal da ASMETRO', upload: 'enviado', caixa: 'e-mail', manual: 'digitado', migrado_papel: 'papel digitalizado', semente: 'semente' };
const EVENTO: Record<string, string> = { mudanca_funcao: 'Mudou de função', mudanca_obra: 'Mudou de obra', afastamento: 'Afastamento', cat: 'CAT', nova_atividade: 'Nova atividade', desligamento: 'Desligamento', readmissao: 'Readmissão' };
const PENDENCIA: Record<string, string> = {
  vencido: 'ASO vencido',
  a_vencer: 'ASO vencendo',
  sem_aso: 'Sem ASO no portal',
  retorno_previsto: 'Exame de retorno ao voltar',
  retorno_atrasado: 'Voltou sem exame de retorno',
  toxicologico_sem_registro: 'Toxicológico sem registro',
  toxicologico_vencido: 'Toxicológico vencido',
  toxicologico_a_vencer: 'Toxicológico vencendo',
  demissional_pendente: 'Demissional pendente',
};
const VALIDADE: Record<Validade, { texto: string; classe: string }> = {
  valido: { texto: 'em dia', classe: 'ok' },
  sem_vencimento: { texto: 'registrado, sem vencimento', classe: 'ok' },
  a_vencer: { texto: 'vence em 30 dias', classe: 'atencao' },
  vencido: { texto: 'VENCIDO', classe: 'alerta' },
  sem_registro: { texto: 'sem registro', classe: 'alerta' },
};

function dataBr(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}
function dias(de: string, ate: string): number {
  return Math.round((Date.parse(`${ate}T12:00:00Z`) - Date.parse(`${de}T12:00:00Z`)) / 86_400_000);
}
function cpfBr(cpf: string): string {
  return cpf.length === 11 ? `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}` : cpf;
}
/** "2 anos e 3 meses", "7 meses", "12 dias". */
function tempo(de: string, ate: string): string {
  const d = dias(de, ate);
  if (d < 31) return `${d} dia${d === 1 ? '' : 's'}`;
  const meses = Math.floor(d / 30.44);
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  if (anos === 0) return `${meses} mes${meses === 1 ? '' : 'es'}`;
  return `${anos} ano${anos === 1 ? '' : 's'}${resto ? ` e ${resto} mes${resto === 1 ? '' : 'es'}` : ''}`;
}
const abrir = (url: string | null, rotulo = '📄 abrir'): string => (url ? `<a href="${escapar(url)}" target="_blank" rel="noopener">${rotulo}</a>` : '');
const vazio = (texto: string): string => `<p class="dica">${texto}</p>`;
const origem = (o: string): string => ORIGEM[o] ?? o;

export function montarFichaSst(raiz: HTMLElement, id: number, voltar: () => void): void {
  let f: Ficha | null = null;
  raiz.innerHTML = `<p class="carregando">Carregando a ficha…</p>`;

  async function carregar(): Promise<void> {
    try {
      f = await api.get<Ficha>(`/api/sst/funcionarios/${id}/ficha`);
      desenhar();
    } catch (erro) {
      raiz.innerHTML = `<p><button class="btn btn-claro" id="sst-voltar">← Voltar</button></p>
        <p class="aviso">${escapar(erro instanceof ErroApi ? erro.message : 'Não consegui abrir a ficha.')}</p>`;
      raiz.querySelector('#sst-voltar')!.addEventListener('click', voltar);
    }
  }

  function desenhar(): void {
    if (!f) return;
    const d = f;
    const p = d.funcionario;
    const ativo = !p.desligamento;

    raiz.innerHTML = `
      <p><button class="btn btn-claro" id="sst-voltar">← Voltar</button></p>
      ${cabecalho(d)}
      ${semaforo(d)}
      ${secaoSaude(d)}
      ${secaoTreinamentos(d)}
      ${secaoEpi(d)}
      ${secaoOsAtividades(d)}
      ${secaoLotacoes(d)}
      ${secaoDocumentos(d)}
      ${secaoPendencias(d)}
      ${secaoLinhaDoTempo(d)}`;

    raiz.querySelector('#sst-voltar')!.addEventListener('click', voltar);
    ligarDocumentos(ativo);
    window.scrollTo({ top: 0 });
  }

  // ------------------------------------------------------------ cabeçalho
  function cabecalho(d: Ficha): string {
    const p = d.funcionario;
    // O inventário do PGR vem como [{grupo: 'ergonômico', agente: 'Postura…'}] (sst/002).
    const riscos = Array.isArray(p.ghe_riscos)
      ? (p.ghe_riscos as unknown[]).map((r) => {
          if (typeof r === 'string') return r;
          const o = (r ?? {}) as Record<string, unknown>;
          const agente = String(o['agente'] ?? o['nome'] ?? o['descricao'] ?? JSON.stringify(r));
          return o['grupo'] ? `${agente} (${String(o['grupo'])})` : agente;
        })
      : [];
    const atividades = [
      ...p.atividades_padrao.map((a) => `${a} <span class="sub-inline">(pela função/GHE)</span>`),
      ...d.atividades.filter((a) => !a.ate).map((a) => `${escapar(a.nome)} <span class="sub-inline">(registrada em ${dataBr(a.desde)})</span>`),
    ];
    const selos = [
      p.desligamento ? `<span class="selo-inativo">desligado em ${dataBr(p.desligamento)}</span>` : '',
      p.afastado ? `<span class="selo-inativo">afastado: ${escapar(CAUSA[p.afastado.motivo ?? ''] ?? p.afastado.motivo ?? '')} desde ${dataBr(p.afastado.inicio)}</span>` : '',
      p.cnh_categoria ? `<span class="etiqueta">CNH ${escapar(p.cnh_categoria)}</span>` : '',
      p.pessoa_nome ? `<span class="etiqueta">usuário do sistema${p.pessoa_email ? `: ${escapar(p.pessoa_email)}` : ''}</span>` : '',
    ].join(' ');
    const linha = (rotulo: string, valor: string): string => (valor ? `<div><dt>${rotulo}</dt><dd>${valor}</dd></div>` : '');
    return `
      <article class="ped-painel sst-ficha">
        <div class="sst-ficha-cab">
          <h2>👷 ${escapar(p.nome)}</h2>
          <span class="sub-inline">${escapar(p.funcao ?? 'função não casada com o PCMSO')}${p.ghe ? ` · GHE ${escapar(p.ghe)} ${escapar(p.ghe_nome ?? '')}` : ''}</span>
          ${selos}
        </div>
        <dl class="sst-ficha-dados">
          ${linha('Obra atual', p.lotacao_atual ? `${escapar(p.lotacao_atual.obra ?? p.lotacao_atual.centro_custo ?? '—')}${p.lotacao_atual.obra_codigo ? ` <span class="sub-inline">(${escapar(p.lotacao_atual.obra_codigo)})</span>` : ''} <span class="sub-inline">desde ${dataBr(p.lotacao_atual.desde)}</span>` : p.desligamento ? '—' : '<span class="v-atencao">sem lotação aberta</span>')}
          ${linha('Centro de custo (Domínio)', escapar(p.lotacao_atual?.centro_custo ?? ''))}
          ${linha('Admissão', p.admissao ? `${dataBr(p.admissao)} <span class="sub-inline">· ${tempo(p.admissao, p.desligamento ?? d.hoje)}${p.desligamento ? ' de casa' : ' de casa até hoje'}</span>` : '—')}
          ${linha('Desligamento', p.desligamento ? `${dataBr(p.desligamento)}${p.retencao_ate ? ` <span class="sub-inline">· guardar o prontuário até ${dataBr(p.retencao_ate)}</span>` : ''}` : '')}
          ${linha('Matrícula no Domínio', escapar(p.matricula_dominio ?? ''))}
          ${linha('CPF', escapar(cpfBr(p.cpf)))}
          ${linha('Nascimento', p.nascimento ? `${dataBr(p.nascimento)}${p.idade !== null ? ` <span class="sub-inline">· ${p.idade} anos</span>` : ''}` : '—')}
          ${linha('Sexo', escapar(p.sexo ?? ''))}
          ${linha('CBO', escapar(p.cbo ?? ''))}
          ${linha('Vínculo', escapar(p.vinculo.toUpperCase()))}
          ${linha('Riscos do GHE', riscos.length ? escapar(riscos.join(', ')) : '')}
          ${linha('Atividades com exigência própria', atividades.length ? atividades.join('<br>') : '')}
          ${linha('Nomes em outros sistemas', d.vinculos.map((v) => `${escapar(v.sistema)}: ${escapar(v.chave)} <span class="sub-inline">(${escapar(v.confianca)})</span>`).join('<br>'))}
          ${linha('Cadastro', `${escapar(origem(p.origem))}${p.importado_em ? `, última carga ${dataHora(p.importado_em)}` : ''}<br><span class="sub-inline">no sistema desde ${dataHora(p.criado_em)} · atualizado ${dataHora(p.atualizado_em)}</span>`)}
          ${linha('Observação', escapar(p.obs ?? ''))}
        </dl>
      </article>`;
  }

  // ------------------------------------------------------------- semáforo
  function semaforo(d: Ficha): string {
    const s = d.semaforo;
    const cartao = (rotulo: string, valor: string, classe: string, sub = ''): string =>
      `<div class="kpi ${classe}"><span class="kpi-rotulo">${rotulo}</span><span class="kpi-valor">${valor}</span>${sub ? `<span class="kpi-sub">${sub}</span>` : ''}</div>`;
    const desligado = !!d.funcionario.desligamento;

    const aso = s.aso;
    const asoTexto = desligado
      ? 'desligado'
      : aso.situacao === 'sem_registro'
        ? 'sem ASO'
        : aso.situacao === 'sem_vencimento'
          ? `exame em ${dataBr(aso.data)}, sem validade`
          : aso.situacao === 'vencido'
            ? `vencido em ${dataBr(aso.validade)}`
            : aso.situacao === 'a_vencer'
              ? `vence em ${dataBr(aso.validade)} (${dias(d.hoje, aso.validade!)} d)`
              : `em dia até ${dataBr(aso.validade)}`;
    // Afastado não examina: vencido vira "exame na volta", como nas pendências.
    const asoClasse = desligado ? '' : aso.situacao === 'valido' ? 'ok' : aso.situacao === 'a_vencer' || aso.situacao === 'sem_vencimento' || s.afastado ? 'atencao' : 'alerta';
    const asoSub = aso.data ? `último: ${escapar(TIPO_EXAME[aso.tipo ?? ''] ?? aso.tipo ?? '')} em ${dataBr(aso.data)}${s.afastado && (aso.situacao === 'vencido' || aso.situacao === 'sem_registro') ? ' · afastado: exame na volta' : ''}` : s.afastado ? 'afastado: exame na volta' : '';

    const t = s.treinamentos;
    const tClasse = t.exigidos === 0 ? '' : t.vencidos + t.sem_registro === 0 ? (t.a_vencer ? 'atencao' : 'ok') : 'alerta';
    const tSub = t.exigidos === 0 ? 'nenhum exigido para a função' : [t.sem_registro ? `${t.sem_registro} sem registro` : '', t.vencidos ? `${t.vencidos} vencido(s)` : '', t.a_vencer ? `${t.a_vencer} vencendo` : ''].filter(Boolean).join(' · ') || 'todos em dia';

    const e = s.epi;
    const eValor = e.sem_matriz ? `${e.em_uso} em uso` : `${e.entregues} de ${e.exigidos} entregues`;
    const eClasse = e.sem_matriz ? '' : e.sem_entrega + e.troca_vencida === 0 ? 'ok' : 'alerta';
    const eSub = e.sem_matriz ? 'sem matriz de EPI cadastrada para a função' : [e.sem_entrega ? `${e.sem_entrega} sem entrega` : '', e.troca_vencida ? `${e.troca_vencida} com troca vencida` : ''].filter(Boolean).join(' · ') || 'tudo entregue';

    return `
      <div class="sst-semaforo">
        ${cartao('ASO', asoTexto, asoClasse, asoSub)}
        ${cartao('Treinamentos', t.exigidos === 0 ? '—' : `${t.validos} de ${t.exigidos} em dia`, tClasse, tSub)}
        ${cartao('EPI', eValor, eClasse, eSub)}
        ${cartao('Pendências abertas', String(s.pendencias), s.pendencias > 0 ? 'alerta' : 'ok', s.pendencias ? 'ver a seção Pendências' : 'nada a fazer')}
        ${s.afastado ? cartao('Afastamento', escapar(CAUSA[s.afastado.motivo ?? ''] ?? s.afastado.motivo ?? 'afastado'), 'atencao', `desde ${dataBr(s.afastado.inicio)}${s.afastado.fim ? ` · volta prevista ${dataBr(s.afastado.fim)}` : ' · sem data de volta'}`) : ''}
        ${s.toxicologico ? cartao('Toxicológico', VALIDADE[s.toxicologico.situacao].texto, VALIDADE[s.toxicologico.situacao].classe, s.toxicologico.ultimo ? `última coleta ${dataBr(s.toxicologico.ultimo)} · vale 30 meses` : 'motorista sem coleta registrada') : ''}
      </div>`;
  }

  // ---------------------------------------------------------------- saúde
  function secaoSaude(d: Ficha): string {
    const asos = d.asos.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr>
          <th>Exame</th><th>Validade</th><th>Resultado</th><th>Médico · clínica</th><th>Origem</th><th>Arquivo</th></tr></thead><tbody>
          ${d.asos
            .map((a) => {
              const restr = Object.entries(a.restricoes ?? {})
                .filter(([, v]) => v !== null)
                .map(([k, v]) => `${v ? '✅' : '⛔'} ${RESTRICAO[k] ?? k}`)
                .join(' · ');
              const venc = a.validade ? (a.validade < d.hoje ? 'v-vencido' : dias(d.hoje, a.validade) <= 30 ? 'v-atencao' : 'v-ok') : '';
              return `<tr><td class="data"><b>${dataBr(a.data_exame)}</b><br><span class="sub-inline">${escapar(TIPO_EXAME[a.tipo] ?? a.tipo)}</span></td>
                <td class="data ${venc}">${a.validade ? dataBr(a.validade) : '<span class="sub-inline">não informada</span>'}</td>
                <td>${a.resultado ? `<b class="${a.resultado === 'inapto' ? 'v-vencido' : a.resultado === 'apto_restricao' ? 'v-atencao' : ''}">${escapar(RESULTADO[a.resultado] ?? a.resultado)}</b>` : '<span class="sub-inline">—</span>'}${restr ? `<br><span class="sub-inline">${restr}</span>` : ''}${a.dispensa_motivo ? `<br><span class="sub-inline">dispensa: ${escapar(a.dispensa_motivo)}</span>` : ''}
                  ${a.exames.length ? `<details><summary class="sub-inline">${a.exames.length} exame(s) complementar(es)</summary><ul class="sst-lista-miuda">${a.exames.map((e) => `<li>${escapar(e.codigo)} ${escapar(e.nome)} · ${dataBr(e.data)}${e.resultado ? ` · <b>${escapar(e.resultado)}</b>` : ''} ${abrir(e.laudo, '📄 laudo')}</li>`).join('')}</ul></details>` : ''}</td>
                <td>${escapar(a.medico_examinador ?? '—')}${a.crm_examinador ? ` <span class="sub-inline">${escapar(a.crm_examinador)}</span>` : ''}${a.clinica ? `<br><span class="sub-inline">${escapar(a.clinica)}</span>` : ''}</td>
                <td class="comp">${escapar(origem(a.origem))}${a.conferido_por ? `<br><span class="sub-inline">conferido por ${escapar(a.conferido_por)}</span>` : ''}${a.requer_revisao ? '<br><span class="selo-inativo">requer revisão</span>' : ''}</td>
                <td>${abrir(a.arquivo) || '<span class="sub-inline">sem PDF</span>'}</td></tr>`;
            })
            .join('')}
        </tbody></table></div>`
      : vazio('Nenhum ASO: o nome não casou com o portal da ASMETRO (veja “Nomes sem vínculo”) ou a pessoa nunca fez exame por lá.');

    const tox = d.toxicologicos.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Coleta</th><th>Tipo</th><th>Resultado</th><th>Laboratório · médico</th><th>eSocial</th><th>Laudo</th></tr></thead><tbody>
          ${d.toxicologicos.map((t) => `<tr><td class="data">${dataBr(t.data_coleta)}${t.data_resultado ? `<br><span class="sub-inline">resultado ${dataBr(t.data_resultado)}</span>` : ''}</td><td>${escapar(t.tipo)}</td>
            <td><b class="${t.resultado === 'positivo' ? 'v-vencido' : ''}">${escapar(t.resultado ?? '—')}</b>${t.contraprova ? ' <span class="sub-inline">· contraprova pedida</span>' : ''}</td>
            <td>${escapar(t.laboratorio ?? '—')}${t.medico ? `<br><span class="sub-inline">${escapar(t.medico)}</span>` : ''}</td><td class="comp">${escapar(t.esocial_status)}</td><td>${abrir(t.arquivo)}</td></tr>`).join('')}
        </tbody></table></div>`
      : '';
    const vac = d.vacinacoes.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Vacina</th><th>Dose</th><th>Data</th><th>Próxima</th><th>Comprovante</th></tr></thead><tbody>
          ${d.vacinacoes.map((v) => `<tr><td>${escapar(v.vacina)}</td><td class="num">${v.dose === 0 ? 'recusa' : v.dose}</td><td class="data">${dataBr(v.data)}</td><td class="data">${dataBr(v.proxima_em)}</td><td>${abrir(v.comprovante)}</td></tr>`).join('')}
        </tbody></table></div>`
      : '';
    const afast = d.afastamentos.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Início</th><th>Fim</th><th>Motivo</th><th>Duração</th><th>Origem</th></tr></thead><tbody>
          ${d.afastamentos.map((a) => `<tr><td class="data">${dataBr(a.inicio)}</td><td class="data">${a.fim ? dataBr(a.fim) : '<span class="v-atencao">em curso</span>'}</td>
            <td>${escapar(CAUSA[a.motivo ?? ''] ?? a.motivo ?? '—')}${a.obs ? `<br><span class="sub-inline">${escapar(a.obs)}</span>` : ''}</td>
            <td class="num">${dias(a.inicio, a.fim ?? d.hoje)} d</td><td class="comp">${escapar(origem(a.origem))}</td></tr>`).join('')}
        </tbody></table></div>`
      : vazio('Nenhum afastamento registrado.');
    const cats = d.cats.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Data</th><th>Tipo · natureza</th><th>O que houve</th><th>Gravidade</th><th>eSocial</th><th>Arquivo</th></tr></thead><tbody>
          ${d.cats.map((c) => `<tr><td class="data">${dataBr(c.data)}${c.hora ? ` ${escapar(c.hora.slice(0, 5))}` : ''}</td><td>${escapar(c.tipo)} · ${escapar(c.natureza)}${c.afastou ? '<br><span class="sub-inline">com afastamento</span>' : ''}</td>
            <td>${escapar(c.descricao ?? '—')}${c.obra ? `<br><span class="sub-inline">${escapar(c.obra)}</span>` : ''}</td><td><b class="${c.gravidade !== 'leve' ? 'v-vencido' : ''}">${escapar(c.gravidade)}</b></td>
            <td class="comp">${escapar(c.esocial_status)}${c.numero_inss ? `<br><span class="sub-inline">${escapar(c.numero_inss)}</span>` : ''}</td><td>${abrir(c.arquivo)}</td></tr>`).join('')}
        </tbody></table></div>`
      : '';

    return `
      <section class="ped-painel"><h3>🩺 Saúde ocupacional</h3>
        <h4>ASOs <span class="sub-inline">${d.asos.length}</span></h4>
        ${asos}
        ${d.semaforo.toxicologico ? `<h4>Toxicológico <span class="sub-inline">${d.toxicologicos.length}</span></h4>${tox || vazio('Motorista sem toxicológico registrado. O formulário de registrar é a próxima rodada.')}` : tox ? `<h4>Toxicológico</h4>${tox}` : ''}
        <h4>Vacinas <span class="sub-inline">${d.vacinacoes.length}</span></h4>
        ${vac || vazio('Nenhuma vacina registrada. O cartão de vacina em papel pode ir para a pasta de documentos.')}
        <h4>Afastamentos <span class="sub-inline">${d.afastamentos.length}</span></h4>
        ${afast}
        <h4>Acidentes (CAT) <span class="sub-inline">${d.cats.length}</span></h4>
        ${cats || vazio('Nenhuma CAT registrada.')}
      </section>`;
  }

  // --------------------------------------------------------- treinamentos
  function secaoTreinamentos(d: Ficha): string {
    const ex = d.exigencias.treinamentos;
    const exigidos = ex.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Treinamento</th><th>Por quê</th><th>Carga · validade</th><th>Último certificado</th><th>Situação</th></tr></thead><tbody>
          ${ex
            .map((x) => `<tr><td class="sst-nome">${escapar(x.nome)}<br><span class="sub-inline">${escapar(x.codigo)}</span></td>
              <td>${x.atividade ? `atividade: ${escapar(x.atividade)}` : 'a função'}${x.exigencia === 'condicional' ? ' <span class="sub-inline">(condicional)</span>' : ''}<br><span class="sub-inline">${escapar(x.doc_tipo === 'norma' ? 'norma' : 'PGR')} · ${escapar(x.procedencia)}</span></td>
              <td class="data">${x.carga_inicial_h ? `${x.carga_inicial_h} h` : '—'}${x.carga_periodica_h ? ` · periódico ${x.carga_periodica_h} h` : ''}<br><span class="sub-inline">${x.validade_meses ? `vale ${x.validade_meses} meses` : 'só vence por evento'}</span></td>
              <td class="data">${x.ultimo_em ? `${dataBr(x.ultimo_em)}${x.valido_ate ? `<br><span class="sub-inline">até ${dataBr(x.valido_ate)}</span>` : ''}` : '—'}</td>
              <td><b class="${x.situacao === 'vencido' || x.situacao === 'sem_registro' ? 'v-vencido' : x.situacao === 'a_vencer' ? 'v-atencao' : 'v-ok'}">${VALIDADE[x.situacao].texto}</b></td></tr>`)
            .join('')}
        </tbody></table></div>`
      : vazio('A matriz legal não exige treinamento para esta função.');
    const certs = d.certificados.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Data</th><th>Treinamento</th><th>Carga · instrutor</th><th>Validade</th><th>Origem</th><th>Arquivo</th></tr></thead><tbody>
          ${d.certificados.map((c) => `<tr><td class="data">${dataBr(c.data)}</td><td class="sst-nome">${escapar(c.treinamento)}<br><span class="sub-inline">${escapar(c.codigo)}${c.origem_certificado === 'convalidado' ? ' · convalidado' : ''}</span></td>
            <td>${c.carga_h ? `${c.carga_h} h` : '—'}${c.instrutor ? `<br><span class="sub-inline">${escapar(c.instrutor)}${c.entidade ? ` · ${escapar(c.entidade)}` : ''}</span>` : c.entidade ? `<br><span class="sub-inline">${escapar(c.entidade)}</span>` : ''}</td>
            <td class="data ${c.validade && c.validade < d.hoje ? 'v-vencido' : ''}">${c.validade ? dataBr(c.validade) : '<span class="sub-inline">só por evento</span>'}</td>
            <td class="comp">${escapar(origem(c.origem))}${c.requer_revisao ? '<br><span class="selo-inativo">requer revisão</span>' : ''}</td><td>${abrir(c.arquivo)}</td></tr>`).join('')}
        </tbody></table></div>`
      : vazio('Nenhum certificado registrado. O formulário de registrar é a próxima rodada; até lá, o certificado em papel pode ir para a pasta de documentos.');
    return `
      <section class="ped-painel"><h3>🎓 Treinamentos</h3>
        <h4>O que a lei e o PGR exigem para esta pessoa <span class="sub-inline">${ex.length}</span></h4>
        ${exigidos}
        <h4>Certificados registrados <span class="sub-inline">${d.certificados.length}</span></h4>
        ${certs}
      </section>`;
  }

  // ------------------------------------------------------------------ EPI
  function secaoEpi(d: Ficha): string {
    const ex = d.exigencias.epi;
    const exigidos = ex.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>EPI</th><th>Por quê</th><th>Última entrega</th><th>Situação</th></tr></thead><tbody>
          ${ex.map((x) => `<tr><td class="sst-nome">${escapar(x.tipo_epi)}${x.quantidade > 1 ? ` <span class="sub-inline">× ${x.quantidade}</span>` : ''}</td>
            <td>${x.atividade ? `atividade: ${escapar(x.atividade)}` : 'a função'}<br><span class="sub-inline">${escapar(x.procedencia)}</span></td>
            <td class="data">${x.entregue_em ? `${dataBr(x.entregue_em)}${x.epi ? `<br><span class="sub-inline">${escapar(x.epi)}</span>` : ''}` : '—'}</td>
            <td><b class="${x.situacao === 'entregue' ? 'v-ok' : 'v-vencido'}">${x.situacao === 'entregue' ? 'entregue' : x.situacao === 'troca_vencida' ? 'troca vencida' : 'sem entrega'}</b></td></tr>`).join('')}
        </tbody></table></div>`
      : vazio('Não há matriz de EPI por função cadastrada ainda (o PGR não traz CA nenhum). Quando houver, aparece aqui o que falta entregar.');
    const entregas = d.epi.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Data</th><th>EPI · CA</th><th>Qtd</th><th>Motivo</th><th>Troca prevista</th><th>Baixa</th><th>Quem · onde</th><th>Termo</th></tr></thead><tbody>
          ${d.epi.map((e) => `<tr><td class="data">${dataBr(e.data)}</td><td class="sst-nome">${escapar(e.epi)}<br><span class="sub-inline">CA ${escapar(e.ca_congelado ?? e.ca ?? '—')}${e.ca_validade_congelada ? ` · válido até ${dataBr(e.ca_validade_congelada)}` : ''}</span></td>
            <td class="num">${e.quantidade}</td><td>${escapar(e.motivo)}</td>
            <td class="data ${e.troca_prevista && !e.baixa && e.troca_prevista < d.hoje ? 'v-vencido' : ''}">${dataBr(e.troca_prevista)}${e.troca_justificativa ? `<br><span class="sub-inline">${escapar(e.troca_justificativa)}</span>` : ''}</td>
            <td>${e.baixa ? `${dataBr(e.baixa.data)} · ${escapar(e.baixa.motivo)}${e.baixa.estado ? ` (${escapar(e.baixa.estado)})` : ''}` : '<span class="v-ok">em uso</span>'}</td>
            <td class="comp">${escapar(e.tecnico ?? '—')}${e.obra ? `<br><span class="sub-inline">${escapar(e.obra)}</span>` : ''}</td><td>${abrir(e.pdf)}</td></tr>`).join('')}
        </tbody></table></div>`
      : vazio('Nenhuma entrega de EPI registrada. O formulário de entrega com assinatura no celular é a próxima rodada; até lá, a ficha em papel pode ir para a pasta de documentos.');
    return `
      <section class="ped-painel"><h3>🦺 EPI</h3>
        <h4>O que a função exige <span class="sub-inline">${ex.length}</span></h4>
        ${exigidos}
        <h4>Entregas <span class="sub-inline">${d.epi.length}</span></h4>
        ${entregas}
      </section>`;
  }

  // ------------------------------------------------- OS e atividades
  function secaoOsAtividades(d: Ficha): string {
    const os = d.ordens_servico.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Gerada em</th><th>Função</th><th>Base</th><th>Validade</th><th>Arquivo</th></tr></thead><tbody>
          ${d.ordens_servico.map((o) => `<tr><td class="data">${dataBr(o.gerada_em)}</td><td>${escapar(o.funcao ?? '—')}</td><td class="comp">${escapar(o.documento ?? '—')}</td>
            <td class="data ${o.validade < d.hoje ? 'v-vencido' : ''}">${dataBr(o.validade)}</td><td>${abrir(o.arquivo)}</td></tr>`).join('')}
        </tbody></table></div>`
      : vazio('Nenhuma ordem de serviço (NR-1) registrada. A OS em papel pode ir para a pasta de documentos.');
    const ativ = d.atividades.length
      ? `<ul class="sst-lista-miuda">${d.atividades.map((a) => `<li>${escapar(a.nome)} · desde ${dataBr(a.desde)}${a.ate ? ` até ${dataBr(a.ate)}` : ' <span class="v-ok">(em curso)</span>'} <span class="sub-inline">${escapar(origem(a.origem))}</span></li>`).join('')}</ul>`
      : vazio('Nenhuma atividade registrada pelo técnico além das que a função e o GHE já trazem.');
    return `
      <section class="ped-painel"><h3>📋 Ordens de serviço e atividades</h3>
        <h4>Ordens de serviço <span class="sub-inline">${d.ordens_servico.length}</span></h4>
        ${os}
        <h4>Atividades registradas para a pessoa <span class="sub-inline">${d.atividades.length}</span></h4>
        ${ativ}
      </section>`;
  }

  // ------------------------------------------------------------- lotações
  function secaoLotacoes(d: Ficha): string {
    return `
      <section class="ped-painel"><h3>🏗️ Obras (lotações) <span class="sub-inline">${d.lotacoes.length}</span></h3>
        <p class="dica">Vem do centro de custo do Domínio, uma linha por período. Serve ao PPP e ao efetivo da obra.</p>
        ${
          d.lotacoes.length
            ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Obra</th><th>Centro de custo</th><th>Início</th><th>Fim</th><th>Período</th></tr></thead><tbody>
              ${d.lotacoes.map((l) => `<tr><td class="sst-nome">${escapar(l.obra ?? '—')}${l.obra_codigo ? ` <span class="sub-inline">${escapar(l.obra_codigo)}</span>` : ''}</td><td class="comp">${escapar(l.centro_custo ?? '—')}</td>
                <td class="data">${dataBr(l.inicio)}</td><td class="data">${l.fim ? dataBr(l.fim) : '<span class="v-ok">atual</span>'}</td><td class="num">${tempo(l.inicio, l.fim ?? d.hoje)}</td></tr>`).join('')}
            </tbody></table></div>`
            : vazio('Nenhuma lotação registrada.')
        }
      </section>`;
  }

  // ----------------------------------------------------------- documentos
  function secaoDocumentos(d: Ficha): string {
    const docs = d.documentos.length
      ? `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Documento</th><th>Data</th><th>Como chegou</th><th>Arquivo</th><th></th></tr></thead><tbody>
          ${d.documentos.map((x) => `<tr><td class="sst-nome">${escapar(NOME_TIPO_DOC.get(x.tipo) ?? x.tipo)}${x.titulo ? `<br><span class="sub-inline">${escapar(x.titulo)}</span>` : ''}${x.obs ? `<br><span class="sub-inline">${escapar(x.obs)}</span>` : ''}</td>
            <td class="data">${dataBr(x.data)}</td>
            <td class="comp">${escapar(origem(x.origem))}${x.enviado_por ? ` por ${escapar(x.enviado_por)}` : ''}<br><span class="sub-inline">${dataHora(x.criado_em)}</span></td>
            <td>${abrir(x.url)} <span class="sub-inline">${x.tipo_mime === 'application/pdf' ? 'PDF' : 'foto'}</span></td>
            <td><button class="btn btn-claro" type="button" data-remover-doc="${x.id}" title="Esconder da pasta (o arquivo fica guardado)">🗑️</button></td></tr>`).join('')}
        </tbody></table></div>`
      : vazio('A pasta está vazia. Envie abaixo, ou mande por e-mail para sistema@ademaldo.com.br com o CPF visível no documento.');
    const SIT: Record<string, string> = { registrado: '✅ registrado', pendente: '⏳ pendente na caixa', duplicado: '↩️ já estava', descartado: '🗑️ descartado' };
    const caixa = d.caixa.length
      ? `<ul class="sst-lista-miuda">${d.caixa.map((c) => `<li>${dataHora(c.recebida_em)} · ${escapar(c.descricao ?? c.nome_arquivo ?? c.tipo)} · de ${escapar(c.remetente_nome ?? c.remetente)} · ${SIT[c.situacao] ?? c.situacao}${c.situacao === 'pendente' ? ' <span class="sub-inline">(confirmar em Administração ▸ Caixa de entrada)</span>' : ''}</li>`).join('')}</ul>`
      : '';
    return `
      <section class="ped-painel"><h3>📁 Pasta de documentos <span class="sub-inline">${d.documentos.length}</span></h3>
        <p class="dica">O que não tem tabela própria: contrato, CTPS, termos, cartão de vacina, rescisão, e o papel digitalizado de certificado, OS e ficha de EPI. Também chega por e-mail em <b>sistema@ademaldo.com.br</b>: com o CPF legível no documento, entra sozinho; sem CPF, fica pendente na caixa de entrada para alguém dizer de quem é.</p>
        ${docs}
        <form class="sst-docs-form" id="sst-doc-form">
          <label>Tipo<select class="campo" name="tipo">${TIPOS_DOCUMENTO.map(([v, n]) => `<option value="${v}">${escapar(n)}</option>`).join('')}</select></label>
          <label>Título (opcional)<input class="campo" name="titulo" maxlength="160" placeholder="ex.: contrato de experiência 45 dias"></label>
          <label>Data do documento<input class="campo" type="date" name="data"></label>
          <label>Arquivo (PDF ou foto)<input class="campo" type="file" name="arquivo" accept="image/*,application/pdf" required></label>
          <button class="btn" type="submit">📎 Guardar na pasta</button>
        </form>
        ${caixa ? `<h4>Pela caixa de entrada <span class="sub-inline">${d.caixa.length}</span></h4>${caixa}` : ''}
      </section>`;
  }

  function ligarDocumentos(ativo: boolean): void {
    void ativo;
    const form = raiz.querySelector<HTMLFormElement>('#sst-doc-form');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const botao = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
      const arquivo = form.querySelector<HTMLInputElement>('input[name=arquivo]')!.files?.[0];
      if (!arquivo) return avisarBloqueante('Escolha o arquivo.');
      // Os campos de texto vão ANTES do arquivo: o servidor para de ler no arquivo.
      const corpo = new FormData();
      corpo.append('tipo', form.querySelector<HTMLSelectElement>('select[name=tipo]')!.value);
      corpo.append('titulo', form.querySelector<HTMLInputElement>('input[name=titulo]')!.value.trim());
      corpo.append('data', form.querySelector<HTMLInputElement>('input[name=data]')!.value);
      corpo.append('arquivo', arquivo);
      botao.disabled = true;
      botao.textContent = '⏳ Enviando…';
      void api
        .enviarMultipart(`/api/sst/funcionarios/${id}/documentos`, corpo)
        .then(() => {
          avisar('Documento guardado na pasta.');
          return carregar();
        })
        .catch((erro) => {
          botao.disabled = false;
          botao.textContent = '📎 Guardar na pasta';
          avisarBloqueante(erro instanceof ErroApi ? erro.message : 'Não consegui guardar o documento.');
        });
    });
    raiz.querySelectorAll<HTMLButtonElement>('[data-remover-doc]').forEach((b) =>
      b.addEventListener('click', () => {
        if (!window.confirm('Esconder este documento da pasta? O arquivo continua guardado no sistema.')) return;
        void api
          .del(`/api/sst/documentos/${b.dataset['removerDoc']}`)
          .then(() => {
            avisar('Documento escondido.');
            return carregar();
          })
          .catch((erro) => avisarBloqueante(erro instanceof ErroApi ? erro.message : 'Não consegui remover.'));
      }),
    );
  }

  // ----------------------------------------------------------- pendências
  function pendenciaTexto(p: Ficha['pendencias'][number]): string {
    const r = p.referencia;
    const motivo = String(r['motivo'] ?? p.tipo);
    return `${escapar(PENDENCIA[motivo] ?? motivo)}${r['base'] ? `<br><span class="sub-inline">Base: ${escapar(String(r['base']))}</span>` : ''}`;
  }
  function secaoPendencias(d: Ficha): string {
    const abertas = d.pendencias.filter((p) => !p.resolvida_em);
    const resolvidas = d.pendencias.filter((p) => p.resolvida_em);
    const tabela = (lista: Ficha['pendencias'], comResolucao: boolean): string =>
      `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>O quê</th><th>${comResolucao ? 'Resolvida' : 'Prazo'}</th><th>Aberta em</th></tr></thead><tbody>
        ${lista
          .map((p) => `<tr><td>${pendenciaTexto(p)}</td>
            <td class="data ${!comResolucao && p.vence_em && p.vence_em < d.hoje ? 'v-vencido' : ''}">${comResolucao ? `${dataHora(p.resolvida_em!)}${p.resolvida_por ? `<br><span class="sub-inline">por ${escapar(p.resolvida_por)}</span>` : ''}` : dataBr(p.vence_em)}</td>
            <td class="data">${dataHora(p.criada_em)}</td></tr>`)
          .join('')}
      </tbody></table></div>`;
    return `
      <section class="ped-painel"><h3>🔔 Pendências</h3>
        <h4>Abertas <span class="sub-inline">${abertas.length}</span></h4>
        ${abertas.length ? tabela(abertas, false) : vazio('Nada pendente. 👌')}
        ${resolvidas.length ? `<h4>Resolvidas <span class="sub-inline">${resolvidas.length}</span></h4>${tabela(resolvidas, true)}` : ''}
      </section>`;
  }

  // -------------------------------------------------------- linha do tempo
  function secaoLinhaDoTempo(d: Ficha): string {
    const p = d.funcionario;
    const itens: { quando: string; texto: string }[] = [];
    const add = (quando: string | null | undefined, texto: string): void => {
      if (quando) itens.push({ quando: quando.slice(0, 10), texto });
    };
    add(p.admissao, '🟢 Admissão');
    add(p.desligamento, '🔴 Desligamento');
    for (const l of d.lotacoes) {
      add(l.inicio, `🏗️ Lotado em ${escapar(l.obra ?? l.centro_custo ?? '—')}`);
      add(l.fim, `🏗️ Saiu de ${escapar(l.obra ?? l.centro_custo ?? '—')}`);
    }
    for (const e of d.eventos) {
      const de = e.dados['de'];
      const para = e.dados['para'];
      add(e.data, `🔁 ${escapar(EVENTO[e.tipo] ?? e.tipo)}${de || para ? ` <span class="sub-inline">${escapar(String(de ?? '—'))} → ${escapar(String(para ?? '—'))}</span>` : ''}`);
    }
    for (const a of d.asos) add(a.data_exame, `🩺 ASO ${escapar(TIPO_EXAME[a.tipo] ?? a.tipo)}${a.resultado ? ` · ${escapar(RESULTADO[a.resultado] ?? a.resultado)}` : ''}${a.validade ? ` <span class="sub-inline">válido até ${dataBr(a.validade)}</span>` : ''}`);
    for (const t of d.toxicologicos) add(t.data_coleta, `🧪 Toxicológico ${escapar(t.tipo)}${t.resultado ? ` · ${escapar(t.resultado)}` : ''}`);
    for (const v of d.vacinacoes) add(v.data, `💉 ${escapar(v.vacina)}${v.dose ? ` · dose ${v.dose}` : ' · recusa'}`);
    for (const a of d.afastamentos) {
      add(a.inicio, `🛌 Afastamento por ${escapar(CAUSA[a.motivo ?? ''] ?? a.motivo ?? '—')}`);
      // O Domínio grava a data de fim PREVISTA: no futuro é previsão, não fato.
      add(a.fim, a.fim && a.fim > d.hoje ? `🛌 Volta prevista do afastamento (${dias(a.inicio, a.fim)} dias)` : `🛌 Volta do afastamento (${dias(a.inicio, a.fim!)} dias)`);
    }
    for (const c of d.cats) add(c.data, `🚑 CAT ${escapar(c.tipo)} · ${escapar(c.natureza)}${c.gravidade !== 'leve' ? ` · <b class="v-vencido">${escapar(c.gravidade)}</b>` : ''}`);
    for (const c of d.certificados) add(c.data, `🎓 Certificado ${escapar(c.treinamento)}${c.validade ? ` <span class="sub-inline">até ${dataBr(c.validade)}</span>` : ''}`);
    for (const o of d.ordens_servico) add(o.gerada_em, `📋 Ordem de serviço${o.funcao ? ` · ${escapar(o.funcao)}` : ''}`);
    for (const e of d.epi) {
      add(e.data, `🦺 Entrega de EPI: ${escapar(e.epi)} ×${e.quantidade} (${escapar(e.motivo)})`);
      if (e.baixa) add(e.baixa.data, `🦺 Baixa de EPI: ${escapar(e.epi)} · ${escapar(e.baixa.motivo)}`);
    }
    for (const a of d.atividades) add(a.desde, `🪜 Passou a fazer: ${escapar(a.nome)}`);
    for (const x of d.documentos) add(x.data ?? x.criado_em, `📁 ${escapar(NOME_TIPO_DOC.get(x.tipo) ?? x.tipo)}${x.titulo ? ` · ${escapar(x.titulo)}` : ''}`);
    for (const c of d.caixa) add(c.recebida_em, `📨 Chegou por e-mail: ${escapar(c.descricao ?? c.nome_arquivo ?? c.tipo)}`);
    for (const q of d.pendencias) {
      add(q.criada_em, `🔔 Pendência aberta: ${escapar(PENDENCIA[String(q.referencia['motivo'] ?? '')] ?? String(q.referencia['motivo'] ?? q.tipo))}`);
      add(q.resolvida_em, `✅ Pendência resolvida: ${escapar(PENDENCIA[String(q.referencia['motivo'] ?? '')] ?? String(q.referencia['motivo'] ?? q.tipo))}`);
    }
    add(p.criado_em, '🗂️ Entrou no sistema');
    itens.sort((a, b) => (a.quando < b.quando ? 1 : a.quando > b.quando ? -1 : 0));
    return `
      <section class="ped-painel"><h3>🕓 Linha do tempo <span class="sub-inline">${itens.length}</span></h3>
        <ul class="sst-linha">${itens.map((i) => `<li><span class="data">${dataBr(i.quando)}</span>${i.texto}</li>`).join('')}</ul>
      </section>`;
  }

  void carregar();
}
